<template>
  <div class="min-h-screen bg-gray-50 flex">
    <!-- 移动端遮罩层 -->
    <div 
      v-if="mobileMenuOpen" 
      class="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
      @click="mobileMenuOpen = false"
    ></div>

    <!-- 侧边栏 (手机端) -->
    <aside :class="[
      'fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out md:hidden',
      mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
    ]">
      <!-- 侧边栏头部 -->
      <div class="flex items-center justify-between h-16 px-6 border-b border-gray-200">
        <div class="flex items-center space-x-2">
          <div class="h-8 w-8 rounded bg-primary-600 flex items-center justify-center">
            <span class="text-white font-bold text-sm">K</span>
          </div>
          <span class="font-semibold text-gray-900 text-lg">BUBU.IM</span>
        </div>
        <button 
          @click="mobileMenuOpen = false"
          class="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-0"
        >
          <XMarkIcon class="h-6 w-6" />
        </button>
      </div>

      <!-- 侧边栏导航 -->
      <nav class="flex-1 px-4 py-4 space-y-2">
        <!-- 按顺序渲染所有导航项 -->
        <template v-for="item in navigation" :key="item.name">
          <!-- 普通导航项 -->
          <button
            v-if="!item.isDropdown"
            @click="openTab(item); mobileMenuOpen = false"
            :class="[
              'w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-0',
              tabsStore.activeTab === item.href
                ? 'text-primary-700 bg-primary-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            ]"
          >
            <component
              :is="item.icon"
              :class="[
                'h-5 w-5 mr-3',
                tabsStore.activeTab === item.href
                  ? 'text-primary-500'
                  : 'text-gray-400'
              ]"
            />
            {{ item.label }}
          </button>
          
          <!-- 下拉菜单项 -->
          <div v-else class="space-y-1">
            <button
              @click="item.name === 'EmailSystem' ? toggleEmailDropdown() : (item.name === 'OrderSystem' ? toggleOrderDropdown() : toggleWebsiteDropdown())"
              :class="[
                'w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-0',
                item.subMenus.some(sub => tabsStore.activeTab === sub.href)
                  ? 'text-primary-700 bg-primary-50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              ]"
            >
              <component
                :is="item.icon"
                :class="[
                  'h-5 w-5 mr-3',
                  item.subMenus.some(sub => tabsStore.activeTab === sub.href)
                    ? 'text-primary-500'
                    : 'text-gray-400'
                ]"
              />
              {{ item.label }}
              <ChevronDownIcon class="h-4 w-4 ml-auto transition-transform duration-200" 
                :class="{ 'rotate-180': (item.name === 'EmailSystem' && emailDropdownOpen) || (item.name === 'OrderSystem' && orderDropdownOpen) || (item.name === 'WebsiteSystem' && websiteDropdownOpen) }" />
            </button>
            
            <!-- 子菜单 -->
            <div v-if="(item.name === 'EmailSystem' && emailDropdownOpen) || (item.name === 'OrderSystem' && orderDropdownOpen) || (item.name === 'WebsiteSystem' && websiteDropdownOpen)" class="ml-8 space-y-1">
              <button
                v-for="subItem in item.subMenus"
                :key="subItem.name"
                @click="openTab(subItem); mobileMenuOpen = false; closeAllDropdowns()"
                :class="[
                  'w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-0',
                  tabsStore.activeTab === subItem.href
                    ? 'text-primary-700 bg-primary-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                ]"
              >
                <component
                  :is="subItem.icon"
                  :class="[
                    'h-4 w-4 mr-3',
                    tabsStore.activeTab === subItem.href
                      ? 'text-primary-500'
                      : 'text-gray-400'
                  ]"
                />
                <span class="relative inline-block">
                  {{ subItem.label }}
                  <span v-if="subItem.badge" class="absolute -top-2 -right-6 inline-flex items-center px-1 py-0 rounded-sm text-[8px] font-bold bg-gradient-to-r from-purple-500 to-indigo-600 text-white leading-tight">{{ subItem.badge }}</span>
                </span>
              </button>
            </div>
          </div>
        </template>
      </nav>

      <!-- 侧边栏底部用户信息 -->
      <div class="border-t border-gray-200 p-4">
        <div class="flex items-center space-x-3">
          <div class="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
            <span class="text-sm font-medium text-gray-700">
              {{ authStore.user?.first_name?.charAt(0) || authStore.user?.username?.charAt(0) || 'U' }}
            </span>
          </div>
          <div class="flex-1">
            <p class="text-sm font-medium text-gray-900">
              {{ authStore.user?.first_name || authStore.user?.username || '用户' }}
            </p>
            <p class="text-xs text-gray-500">{{ authStore.user?.email }}</p>
          </div>
        </div>
        <div class="mt-4 space-y-1">
          <button
            @click="openProfileModal(); mobileMenuOpen = false; closeAllDropdowns()"
            class="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md focus:outline-none focus:ring-0"
          >
            <UserIcon class="h-4 w-4 mr-2 inline" />
            个人资料
          </button>
          <button
            @click="openChangePasswordModal(); mobileMenuOpen = false; closeAllDropdowns()"
            class="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md focus:outline-none focus:ring-0"
          >
            <svg class="h-4 w-4 mr-2 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            修改密码
          </button>
          <button
            @click="handleLogout"
            class="block w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md focus:outline-none focus:ring-0"
          >
            <ArrowRightOnRectangleIcon class="h-4 w-4 mr-2 inline" />
            退出登录
          </button>
        </div>
      </div>
    </aside>

    <!-- 主容器 -->
    <div class="flex-1 flex flex-col w-full h-screen overflow-hidden">
      <!-- 桌面端顶部导航栏 -->
      <header class="hidden md:block bg-white shadow-sm border-b border-gray-200 flex-shrink-0 z-40">
      <div class="px-6">
        <div class="flex items-center justify-between h-16">
          <!-- Logo 和主导航 -->
          <div class="flex items-center space-x-8">
            <!-- Logo -->
            <div class="flex items-center space-x-2">
              <div class="h-8 w-8 rounded bg-primary-600 flex items-center justify-center">
                <span class="text-white font-bold text-sm">B</span>
              </div>
              <span class="font-semibold text-gray-900 text-lg">BUBU.IM</span>
            </div>

            <!-- 主导航菜单 -->
            <nav class="flex space-x-6">
              <!-- 按顺序渲染所有导航项 -->
              <template v-for="item in navigation" :key="item.name">
                <!-- 普通导航项 -->
                <button
                  v-if="!item.isDropdown"
                  @click="openTab(item)"
                  :class="[
                    'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                    tabsStore.activeTab === item.href
                      ? 'text-primary-700 bg-primary-50'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  ]"
                >
                  <component
                    :is="item.icon"
                    :class="[
                      'h-4 w-4 mr-2',
                      tabsStore.activeTab === item.href
                        ? 'text-primary-500'
                        : 'text-gray-400'
                    ]"
                  />
                  {{ item.label }}
                </button>
                
                <!-- 下拉菜单项 -->
                <div v-else class="relative">
                  <button
                    @click="item.name === 'EmailSystem' ? toggleEmailDropdown() : (item.name === 'OrderSystem' ? toggleOrderDropdown() : toggleWebsiteDropdown())"
                    :class="[
                      'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                      item.subMenus.some(sub => tabsStore.activeTab === sub.href)
                        ? 'text-primary-700 bg-primary-50'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    ]"
                  >
                    <component
                      :is="item.icon"
                      :class="[
                        'h-4 w-4 mr-2',
                        item.subMenus.some(sub => tabsStore.activeTab === sub.href)
                          ? 'text-primary-500'
                          : 'text-gray-400'
                      ]"
                    />
                    {{ item.label }}
                    <ChevronDownIcon class="h-4 w-4 ml-1 transition-transform duration-200" 
                      :class="{ 'rotate-180': (item.name === 'EmailSystem' && emailDropdownOpen) || (item.name === 'OrderSystem' && orderDropdownOpen) || (item.name === 'WebsiteSystem' && websiteDropdownOpen) }" />
                  </button>
                  
                  <!-- 下拉菜单 -->
                  <div
                    v-if="(item.name === 'EmailSystem' && emailDropdownOpen) || (item.name === 'OrderSystem' && orderDropdownOpen) || (item.name === 'WebsiteSystem' && websiteDropdownOpen)"
                    v-click-outside="closeAllDropdowns"
                    class="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200"
                  >
                    <button
                      v-for="subItem in item.subMenus"
                      :key="subItem.name"
                      @click="openTab(subItem); closeAllDropdowns()"
                      :class="[
                        'w-full text-left px-4 py-2 text-sm flex items-center transition-colors',
                        tabsStore.activeTab === subItem.href
                          ? 'text-primary-700 bg-primary-50'
                          : 'text-gray-700 hover:bg-gray-50'
                      ]"
                    >
                      <component
                        :is="subItem.icon"
                        :class="[
                          'h-4 w-4 mr-3',
                          tabsStore.activeTab === subItem.href
                            ? 'text-primary-500'
                            : 'text-gray-400'
                        ]"
                      />
                      <span class="relative inline-block">
                        {{ subItem.label }}
                        <span v-if="subItem.badge" class="absolute -top-2 -right-6 inline-flex items-center px-1 py-0 rounded-sm text-[8px] font-bold bg-gradient-to-r from-purple-500 to-indigo-600 text-white leading-tight">{{ subItem.badge }}</span>
                      </span>
                    </button>
                  </div>
                </div>
              </template>
            </nav>
          </div>

          <!-- 右侧工具栏 -->
          <div class="flex items-center">
            <!-- 用户菜单 -->
            <div class="relative">
              <button
                @click="toggleUserMenu()"
                class="flex items-center space-x-3 text-gray-700 hover:text-gray-900 focus:outline-none focus:ring-0 rounded-md p-1"
              >
                <div class="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                  <span class="text-sm font-medium text-gray-700">
                    {{ getUserInitial() }}
                  </span>
                </div>
                <div class="hidden md:block text-left">
                  <div class="text-sm font-medium text-gray-900">
                    {{ getUserDisplayName() }}
                  </div>
                  <div class="text-xs text-gray-500">
                    {{ authStore.user?.email || '' }}
                  </div>
                </div>
                <ChevronDownIcon class="h-4 w-4 text-gray-400" />
              </button>

              <!-- 下拉菜单 -->
              <div
                v-if="userMenuOpen"
                v-click-outside="closeAllDropdowns"
                class="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200"
              >
                <button
                  @click="openProfileModal(); closeAllDropdowns()"
                  class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-0"
                >
                  <UserIcon class="h-4 w-4 mr-2 inline" />
                  个人资料
                </button>
                <button
                  @click="openChangePasswordModal(); closeAllDropdowns()"
                  class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-0"
                >
                    <svg class="h-4 w-4 mr-2 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1 1 21 9z" />
                    </svg>
                  修改密码
                </button>
                <button
                  @click="openWechatModal(); closeAllDropdowns()"
                  class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-0"
                >
                  <svg class="h-4 w-4 mr-2 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  微信通知
                </button>
                <hr class="my-1">
                <button
                  @click="handleLogout"
                  class="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 focus:outline-none focus:ring-0"
                >
                  <ArrowRightOnRectangleIcon class="h-4 w-4 mr-2 inline" />
                  退出登录
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      </header>

      <!-- 手机端顶部栏 -->
      <div class="md:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0 z-30">
        <button
          @click="mobileMenuOpen = true"
          class="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-0"
        >
          <Bars3Icon class="h-6 w-6" />
        </button>
        <div class="flex items-center space-x-2">
          <div class="h-6 w-6 rounded bg-primary-600 flex items-center justify-center">
            <span class="text-white font-bold text-xs">B</span>
          </div>
          <span class="font-semibold text-gray-900">BUBU.IM</span>
        </div>
        <button
          @click="toggleMobileUserMenu()"
          class="relative focus:outline-none focus:ring-0"
        >
          <div class="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
            <span class="text-xs font-medium text-gray-700">
              {{ getUserInitial() }}
            </span>
          </div>
          <!-- 手机端用户菜单 -->
          <div
            v-if="mobileUserMenuOpen"
            v-click-outside="closeAllDropdowns"
            class="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200"
          >
            <button
              @click="openProfileModal(); closeAllDropdowns()"
              class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-0"
            >
              <UserIcon class="h-4 w-4 mr-2 inline" />
              个人资料
            </button>
            <button
              @click="openChangePasswordModal(); closeAllDropdowns()"
              class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-0"
            >
              <svg class="h-4 w-4 mr-2 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
              修改密码
            </button>
            <button
              @click="openWechatModal(); closeAllDropdowns()"
              class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-0"
            >
              <svg class="h-4 w-4 mr-2 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              微信通知
            </button>
            <hr class="my-1">
            <button
              @click="handleLogout"
              class="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 focus:outline-none focus:ring-0"
            >
              <ArrowRightOnRectangleIcon class="h-4 w-4 mr-2 inline" />
              退出登录
            </button>
          </div>
        </button>
      </div>

      <!-- 标签页栏 - 响应式设计 -->
      <div class="bg-white border-b border-gray-200 flex-shrink-0 z-20">
        <div class="px-4 md:px-6">
          <div class="flex items-center overflow-x-auto scrollbar-hide">
            <div class="flex space-x-0.5 py-2">
              <div
                v-for="tab in tabsStore.tabs"
                :key="tab.path"
                :class="[
                  'group relative flex items-center rounded-md cursor-pointer tab-item whitespace-nowrap',
                  'px-1.5 py-1 pr-3 text-xs md:px-2 md:py-1 md:pr-4 md:text-sm',
                  tab.path === tabsStore.activeTab
                    ? 'bg-primary-50 text-primary-700 border border-primary-200 tab-active'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 border border-transparent'
                ]"
                @click="switchTab(tab.path)"
              >
                <component
                  :is="getIconComponent(tab.icon)"
                  :class="[
                    'h-3 w-3 md:h-3.5 md:w-3.5 mr-1 md:mr-1.5',
                    tab.path === tabsStore.activeTab ? 'text-primary-500' : 'text-gray-400'
                  ]"
                />
                <!-- 桌面端显示完整标题，手机端显示缩写 -->
                <span class="hidden sm:inline-flex items-start gap-0.5">
                  <span>{{ tab.path === '/qr-code-pro' ? tab.title.replace(/\s*PRO\s*$/i, '') : tab.title }}</span>
                  <span v-if="tab.badge || tab.path === '/qr-code-pro'" class="inline-flex items-center p-[0.1rem] rounded-sm text-[8px] font-bold bg-gradient-to-r from-purple-500 to-indigo-600 text-white leading-tight -mt-0.5 ml-0.5">{{ tab.badge || 'PRO' }}</span>
                </span>
                <span class="sm:hidden inline-flex items-start gap-0.5">
                  <span>{{ getShortTitle(tab.path === '/qr-code-pro' ? tab.title.replace(/\s*PRO\s*$/i, '') : tab.title) }}</span>
                  <span v-if="tab.badge || tab.path === '/qr-code-pro'" class="inline-flex items-center px-0.5 rounded-sm text-[7px] font-bold bg-gradient-to-r from-purple-500 to-indigo-600 text-white leading-tight -mt-0.5">{{ tab.badge || 'PRO' }}</span>
                </span>
                
                <!-- 刷新按钮（固定显示） -->
                <button
                  @click.stop="refreshTab(tab.path)"
                  class="ml-1 h-3.5 w-3.5 md:h-4 md:w-4 rounded-sm flex items-center justify-center tab-refresh-btn focus:outline-none focus:ring-0"
                  title="刷新标签页"
                >
                  <ArrowPathIcon class="h-2.5 w-2.5 md:h-3 md:w-3" />
                </button>
                
                <!-- 关闭按钮（悬停时在右上角显示） -->
                <button
                  v-if="tab.closable"
                  @click.stop="closeTab(tab.path)"
                  class="absolute -top-1 -right-1 h-3.5 w-3.5 rounded-full bg-gray-400 hover:bg-gray-500 text-white flex items-center justify-center tab-close-btn focus:outline-none focus:ring-0 shadow-sm"
                  title="关闭标签页"
                >
                  <XMarkIcon class="h-2 w-2" />
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>

      <!-- 主内容区域 - 独立滚动容器 -->
      <main class="flex-1 overflow-y-auto overflow-x-hidden">
        <!-- 内容框架容器 -->
        <div class="w-full relative">
          <!-- 当前活跃标签页的内容 -->
          <div
            v-for="tab in tabsStore.tabs"
            :key="tab.path"
            v-show="tab.path === tabsStore.activeTab"
            class="w-full"
          >
            <div class="p-4">
              <!-- 动态组件渲染 -->
              <component
                :is="getTabComponent(tab.path)"
                :key="`${tab.path}_${tab.refreshKey || 0}`"
              />
            </div>
          </div>

          <!-- 空状态 - 当没有打开任何标签页时 -->
          <div
            v-if="tabsStore.tabs.length === 0"
            class="min-h-[60vh] flex items-center justify-center text-gray-500 p-4"
          >
            <div class="text-center">
              <HomeIcon class="mx-auto h-12 w-12 md:h-16 md:w-16 text-gray-300" />
              <h3 class="mt-4 text-base md:text-lg font-medium text-gray-900">欢迎使用 BUBU.IM</h3>
              <p class="mt-2 text-xs md:text-sm text-gray-500">
                <span class="hidden md:inline">选择上方导航菜单开始使用系统功能</span>
                <span class="md:hidden">点击左上角菜单开始使用</span>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>

    <!-- 个人资料模态框 -->
    <ProfileModal
      :show="showProfileModal"
      @close="closeProfileModal"
    />

    <!-- 修改密码模态框 -->
    <ChangePasswordModal
      :show="showChangePasswordModal"
      @close="closeChangePasswordModal"
    />

    <!-- 微信通知模态框 -->
    <WechatNotificationModal
      :show="showWechatModal"
      @close="closeWechatModal"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useTabsStore } from '@/stores/tabs'
import {
  ChevronDownIcon,
  XMarkIcon,
  ArrowPathIcon,
  ArrowRightOnRectangleIcon,
  HomeIcon,
  UsersIcon,
  EnvelopeIcon,
  InboxIcon,
  DocumentTextIcon,
  MegaphoneIcon,
  ShoppingBagIcon,
  UserIcon,
  Bars3Icon,
  GlobeAltIcon,
  QrCodeIcon,
  BuildingStorefrontIcon,
  UserGroupIcon,
  ShieldExclamationIcon,
  ClockIcon
} from '@heroicons/vue/24/outline'

// 引入页面组件
import Dashboard from '@/views/Dashboard.vue'
import CustomerList from '@/views/customers/CustomerList.vue'
import CustomerDetail from '@/views/customers/CustomerDetail.vue'
import EmailAccountList from '@/views/email/EmailAccountList.vue'
import EmailList from '@/views/email/EmailList.vue'
import CampaignList from '@/views/campaigns/CampaignList.vue'
import TemplateList from '@/views/templates/TemplateList.vue'
import ProductList from '@/views/products/ProductList.vue'
import QrCodeList from '@/views/qr-codes/QrCodeList.vue'
import QrCodeProList from '@/views/qr-codes-pro/QrCodeProList.vue'
import BrandSiteComingSoon from '@/views/brand-site/BrandSiteComingSoon.vue'
import SubAccountManagement from '@/views/user-management/SubAccountManagement.vue'
import SensitiveWordList from '@/views/sensitive-words/SensitiveWordList.vue'
import AsyncEmailTaskList from '@/views/email/AsyncEmailTaskList.vue'
import ExternalOrderList from '@/views/orders/ExternalOrderList.vue'

// 引入模态框组件
import ProfileModal from '@/components/auth/ProfileModal.vue'
import ChangePasswordModal from '@/components/auth/ChangePasswordModal.vue'
import WechatNotificationModal from '@/components/auth/WechatNotificationModal.vue'

const router = useRouter()
const authStore = useAuthStore()
const tabsStore = useTabsStore()

const userMenuOpen = ref(false)
const mobileUserMenuOpen = ref(false)
const mobileMenuOpen = ref(false)
const emailDropdownOpen = ref(false)
const orderDropdownOpen = ref(false)
const websiteDropdownOpen = ref(false)
const showProfileModal = ref(false)
const showChangePasswordModal = ref(false)
const showWechatModal = ref(false)

// 邮件系统子菜单配置
const emailSubMenus = [
  { 
    name: 'EmailAccounts', 
    label: '邮件账户', 
    href: '/email-accounts', 
    icon: EnvelopeIcon,
    component: 'EmailAccountList'
  },
  { 
    name: 'Emails', 
    label: '邮件管理', 
    href: '/emails', 
    icon: InboxIcon,
    component: 'EmailList'
  },
  { 
    name: 'Templates', 
    label: '邮件模板', 
    href: '/templates', 
    icon: DocumentTextIcon,
    component: 'TemplateList'
  },
  { 
    name: 'Campaigns', 
    label: '邮件营销', 
    href: '/campaigns', 
    icon: MegaphoneIcon,
    component: 'CampaignList'
  },
  { 
    name: 'SensitiveWords', 
    label: '敏感词库', 
    href: '/sensitive-words', 
    icon: ShieldExclamationIcon,
    component: 'SensitiveWordList'
  },
  { 
    name: 'AsyncEmailTasks', 
    label: '异步任务', 
    href: '/async-email-tasks', 
    icon: ClockIcon,
    component: 'AsyncEmailTaskList'
  },
]

// 订单系统子菜单配置
const orderSubMenus = [
  { 
    name: 'ExternalOrders', 
    label: '外部订单', 
    href: '/external-orders', 
    icon: ShoppingBagIcon,
    component: 'ExternalOrderList'
  },
]

// 建站系统子菜单配置
const websiteSubMenus = [
  {
    name: 'QrCode',
    label: '售后二维码',
    href: '/qr-code',
    icon: QrCodeIcon,
    component: 'QrCodeList'
  },
  {
    name: 'QrCodePro',
    label: '售后二维码',
    badge: 'PRO',
    href: '/qr-code-pro',
    icon: QrCodeIcon,
    component: 'QrCodeProList'
  },
  {
    name: 'BrandSite',
    label: '品牌独立站',
    href: '/brand-site',
    icon: BuildingStorefrontIcon,
    component: 'BrandSiteList'
  },
]

// 导航菜单基础配置 - 确保用户管理在建站系统后面
const baseNavigation = [
  { 
    name: 'Dashboard', 
    label: '快捷面板', 
    href: '/dashboard', 
    icon: HomeIcon,
    component: 'Dashboard'
  },
  { 
    name: 'Customers', 
    label: '客户管理', 
    href: '/customers', 
    icon: UsersIcon,
    component: 'CustomerList'
  },
  { 
    name: 'Products', 
    label: '产品管理', 
    href: '/products', 
    icon: ShoppingBagIcon,
    component: 'ProductList'
  },
  { 
    name: 'OrderSystem', 
    label: '订单管理', 
    href: '/order-system', 
    icon: ShoppingBagIcon,
    isDropdown: true,
    subMenus: orderSubMenus
  },
  { 
    name: 'EmailSystem', 
    label: '邮件系统', 
    href: '/email-system', 
    icon: EnvelopeIcon,
    isDropdown: true,
    subMenus: emailSubMenus
  },
  { 
    name: 'WebsiteSystem', 
    label: '建站系统', 
    href: '/website-system', 
    icon: GlobeAltIcon,
    isDropdown: true,
    subMenus: websiteSubMenus
  },
  { 
    name: 'UserManagement', 
    label: '用户管理', 
    href: '/user-management', 
    icon: UserGroupIcon,
    component: 'SubAccountManagement'
  }
]

// 根据用户类型过滤导航菜单
const navigation = computed(() => {
  const userAccountType = authStore.user?.account_type || 'master'
  
  return baseNavigation.filter(item => {
    // 子账户不显示用户管理
    if (item.name === 'UserManagement' && userAccountType === 'sub') {
      return false
    }
    return true
  })
})

// 组件映射
const componentMap = {
  Dashboard,
  CustomerList,
  CustomerDetail,
  EmailAccountList,
  EmailList,
  CampaignList,
  TemplateList,
  ProductList,
  QrCodeList,
  QrCodeProList
}

// 临时占位符组件
const QrCodePlaceholder = {
  template: `
    <div class="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div class="px-4 py-6 sm:px-0">
        <div class="border-4 border-dashed border-gray-200 rounded-lg h-96 flex items-center justify-center">
          <div class="text-center">
            <QrCodeIcon class="mx-auto h-12 w-12 text-gray-400" />
            <h3 class="mt-2 text-sm font-medium text-gray-900">售后二维码</h3>
            <p class="mt-1 text-sm text-gray-500">此功能即将推出</p>
          </div>
        </div>
      </div>
    </div>
  `,
  components: { QrCodeIcon }
}

// 根据路径获取对应的组件
const getTabComponent = (path) => {
  const componentMapping = {
    '/dashboard': Dashboard,
    '/customers': CustomerList,
    '/email-accounts': EmailAccountList,
    '/emails': EmailList,
    '/templates': TemplateList,
    '/campaigns': CampaignList,
    '/sensitive-words': SensitiveWordList,
    '/async-email-tasks': AsyncEmailTaskList,
    '/products': ProductList,
    '/external-orders': ExternalOrderList,
    '/qr-code': QrCodeList,
    '/qr-code-pro': QrCodeProList,
    '/brand-site': BrandSiteComingSoon,
    '/user-management': SubAccountManagement
  }
  
  // 处理动态路由
  if (path.startsWith('/customers/') && path !== '/customers') {
    return CustomerDetail
  }
  
  return componentMapping[path] || Dashboard
}

// 图标组件映射
const iconComponents = {
  HomeIcon,
  UsersIcon,
  EnvelopeIcon,
  InboxIcon,
  DocumentTextIcon,
  MegaphoneIcon,
  ShoppingBagIcon,
  UserIcon,
  GlobeAltIcon,
  QrCodeIcon,
  BuildingStorefrontIcon,
  UserGroupIcon,
  ShieldExclamationIcon,
  ClockIcon
}

const getIconComponent = (iconName) => {
  return iconComponents[iconName] || DocumentTextIcon
}

// 获取标签页缩写标题（手机端使用）
const getShortTitle = (title) => {
  const shortTitles = {
    '快捷面板': '快捷面板',
    '客户管理': '客户管理',
    '邮件账户': '邮件账户',
    '邮件管理': '邮件管理',
    '邮件模板': '邮件模板',
    '邮件营销': '邮件营销',
    '产品管理': '产品管理',
    '售后二维码': '售后二维码',
    '品牌独立站': '品牌独立站',
    '用户管理': '用户管理',
    '个人资料': '个人资料',
    '敏感词库': '敏感词库',
    '异步任务': '异步任务'
  }
  return shortTitles[title] || title.substring(0, 2)
}

// 获取用户头像缩写
const getUserInitial = () => {
  const user = authStore.user
  if (user?.first_name) return user.first_name.charAt(0).toUpperCase()
  if (user?.last_name) return user.last_name.charAt(0).toUpperCase()
  if (user?.username) return user.username.charAt(0).toUpperCase()
  return 'U'
}

// 获取用户显示名称
const getUserDisplayName = () => {
  const user = authStore.user
  if (user?.first_name && user?.last_name) {
    return `${user.first_name} ${user.last_name}`
  }
  if (user?.first_name) return user.first_name
  if (user?.last_name) return user.last_name
  if (user?.username) return user.username
  return '用户'
}

// 移除内容区域高度计算，现在使用 flex 布局自动分配空间

// 打开标签页
const openTab = (item) => {
  // 添加标签页到store
  tabsStore.addTab({
    path: item.href,
    name: item.name,
    params: {},
    query: {}
  })
  
  // 不需要路由跳转，直接切换标签页显示
}

// 切换标签页
const switchTab = (path) => {
  tabsStore.switchToTab(path)
}

// 关闭标签页
const closeTab = (path) => {
  tabsStore.closeTab(path)
}


// 刷新指定标签页
const refreshTab = (tabPath) => {
  // 直接调用store的refreshTab方法，通过更新refreshKey来触发组件重新渲染
  tabsStore.refreshTab(tabPath)
}

// 登出
const handleLogout = () => {
  authStore.logout()
  router.push('/login')
  closeAllDropdowns()
  mobileMenuOpen.value = false
}

// 点击外部关闭菜单的指令 - 修复版本
const vClickOutside = {
  mounted(el, binding) {
    el.clickOutsideEvent = (event) => {
      // 确保点击的不是按钮本身，也不是菜单内部
      if (!el.contains(event.target)) {
        binding.value()
      }
    }
    // 立即添加事件监听器，使用 nextTick 确保DOM已更新
    document.addEventListener('click', el.clickOutsideEvent, true)
  },
  unmounted(el) {
    if (el.clickOutsideEvent) {
      document.removeEventListener('click', el.clickOutsideEvent, true)
    }
  }
}

// 关闭所有下拉菜单
const closeAllDropdowns = () => {
  emailDropdownOpen.value = false
  orderDropdownOpen.value = false
  websiteDropdownOpen.value = false
  userMenuOpen.value = false
  mobileUserMenuOpen.value = false
}

// 切换下拉菜单的辅助方法
const toggleDropdown = (dropdownType) => {
  // 先关闭其他所有下拉菜单
  const currentState = {
    email: emailDropdownOpen.value,
    order: orderDropdownOpen.value,
    website: websiteDropdownOpen.value,
    user: userMenuOpen.value,
    mobileUser: mobileUserMenuOpen.value
  }
  
  // 关闭所有下拉菜单
  closeAllDropdowns()
  
  // 然后切换指定的下拉菜单
  switch(dropdownType) {
    case 'email':
      emailDropdownOpen.value = !currentState.email
      break
    case 'order':
      orderDropdownOpen.value = !currentState.order
      break
    case 'website':  
      websiteDropdownOpen.value = !currentState.website
      break
    case 'user':
      userMenuOpen.value = !currentState.user
      break
    case 'mobileUser':
      mobileUserMenuOpen.value = !currentState.mobileUser
      break
  }
}

// 专门的下拉菜单切换方法
const toggleEmailDropdown = () => toggleDropdown('email')
const toggleOrderDropdown = () => toggleDropdown('order')
const toggleWebsiteDropdown = () => toggleDropdown('website') 
const toggleUserMenu = () => toggleDropdown('user')
const toggleMobileUserMenu = () => toggleDropdown('mobileUser')

// 个人资料模态框方法
const openProfileModal = () => {
  showProfileModal.value = true
  closeAllDropdowns()
}

const closeProfileModal = () => {
  showProfileModal.value = false
}

// 修改密码模态框方法
const openChangePasswordModal = () => {
  showChangePasswordModal.value = true
  closeAllDropdowns()
}

const closeChangePasswordModal = () => {
  showChangePasswordModal.value = false
}

// 微信通知模态框方法
const openWechatModal = () => {
  showWechatModal.value = true
  closeAllDropdowns()
}

const closeWechatModal = () => {
  showWechatModal.value = false
}

// 初始化默认标签页
const initDefaultTab = () => {
  if (tabsStore.tabs.length === 0) {
    openTab({
      name: 'Dashboard',
      label: '快捷面板', 
      href: '/dashboard',
      icon: HomeIcon
    })
  }
}

// 组件挂载时初始化
import { onMounted } from 'vue'
onMounted(async () => {
  // 初始化认证状态
  await authStore.initAuth()
  // 然后初始化默认标签页
  initDefaultTab()
})
</script>

<style scoped>
/* 隐藏标签页栏的滚动条 */
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

/* 标签页的简洁美观样式 */
.tab-content {
  border: none;
  background: white;
}

/* 标签页整体布局优化 */
.tab-item {
  max-width: 200px; /* 限制标签页最大宽度 */
  min-width: 80px;  /* 确保最小可用宽度 */
}

.tab-item span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 标签页悬停效果优化 */
.group:hover .opacity-0 {
  opacity: 1;
}

/* 活跃标签页特殊样式 */
.tab-active {
  background: linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%);
  border-color: #3b82f6;
  box-shadow: 0 1px 3px rgba(59, 130, 246, 0.1);
}

/* 标签页刷新按钮优化（固定显示） */
.tab-refresh-btn {
  transition: all 0.15s ease;
  background-color: rgba(107, 114, 128, 0.08);
  color: #6b7280;
}

.tab-refresh-btn:hover {
  background-color: rgba(59, 130, 246, 0.15);
  color: #2563eb;
  transform: scale(1.05);
}

/* 标签页关闭按钮优化（悬停显示） */
.tab-close-btn {
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;
  transform: scale(0.8);
  z-index: 10;
}

.group:hover .tab-close-btn {
  opacity: 1;
  visibility: visible;
  transform: scale(1);
}

.tab-close-btn:hover {
  transform: scale(1.1);
}

/* 活跃标签页的刷新按钮样式 */
.tab-active .tab-refresh-btn {
  background-color: rgba(59, 130, 246, 0.08);
  color: #3b82f6;
}

/* 移除响应式高度计算，现在使用 flex 布局自动分配 */

/* 美化主内容区域的滚动条 */
main::-webkit-scrollbar {
  width: 6px;
}

main::-webkit-scrollbar-track {
  background: #f8fafc;
  border-radius: 3px;
}

main::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
  transition: background 0.2s ease;
}

main::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Firefox 滚动条样式 */
main {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f8fafc;
}

/* 标签页平滑过渡动画 */
.tab-item {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.tab-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}


/* 手机端侧边栏动画 */
.sidebar-enter-active,
.sidebar-leave-active {
  transition: transform 0.3s ease-in-out;
}

.sidebar-enter-from,
.sidebar-leave-to {
  transform: translateX(-100%);
}

/* 手机端优化 */
@media (max-width: 767px) {
  /* 手机端标签页紧凑布局 */
  .tab-item {
    min-width: auto;
    flex-shrink: 0;
    padding: 0.25rem 0.75rem 0.25rem 0.375rem;
  }
  
  /* 手机端按钮优化 - 关闭按钮始终显示 */
  .tab-close-btn {
    opacity: 1;
    visibility: visible;
    transform: scale(1);
  }
  
  /* 手机端滚动条隐藏 */
  .scrollbar-hide {
    -webkit-overflow-scrolling: touch;
  }
  
  /* 手机端触摸优化 */
  .mobile-touch {
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
  }
}

/* 平板端优化 */
@media (min-width: 768px) and (max-width: 1023px) {
  /* 平板端导航栏适配 */
  .desktop-nav > * + * {
    margin-left: 1rem;
  }
  
  /* 标签页适中大小 */
  .tab-item {
    padding: 0.375rem 0.75rem;
  }
}

/* 桌面端优化 */
@media (min-width: 1024px) {
  /* 桌面端完整布局 */
  .desktop-nav > * + * {
    margin-left: 1.5rem;
  }
  
  /* 标签页舒适间距 */
  .tab-item {
    padding: 0.375rem 1rem;
  }
}

/* 移除所有按钮和可点击元素的聚焦边框 */
button:focus,
button:focus-visible,
[role="button"]:focus,
[role="button"]:focus-visible {
  outline: none !important;
  box-shadow: none !important;
  border-color: transparent !important;
}

/* 移除输入框的聚焦边框 */
input:focus,
textarea:focus,
select:focus {
  outline: none !important;
  box-shadow: none !important;
  border-color: #d1d5db !important;
}
</style>
